import type { ActionStatus } from './schemas/actions.js';
import type { ActionConsequence, ConsequenceKind } from './schemas/plans.js';

/**
 * Lot T « Être prévenu des cartes ajoutées ou retirées par une action » (demande de Maxime du 13/10/2026) :
 * logique pure du diff d'un passage des règles (cartes créées, fermées d'elles-mêmes, rouvertes,
 * réattribuées), du regroupement par personne pour les notifications (jamais l'auteur du geste) et des
 * textes affichés (résultats de l'écran de validation, toast, notification). Une notification INFORME.
 */

const OPEN: readonly ActionStatus[] = ['OPEN', 'WAITING_REPLY', 'REPLY_TO_HANDLE'];
const isOpen = (status: ActionStatus) => OPEN.includes(status);

/** État d'une action avant ou après un passage des règles (ce que le diff compare). */
export interface ActionState {
  id: string;
  status: ActionStatus;
  assigneeId: string | null;
  title: string;
  ruleKey: string | null;
}

/** Changement d'une carte par un passage des règles. */
export interface RulesChange {
  kind: ConsequenceKind;
  actionId: string;
  title: string;
  assigneeId: string | null;
  previousAssigneeId: string | null;
  ruleKey: string | null;
  reason: string;
}

export interface DiffReasons {
  /** Pourquoi la carte apparaît (le « why » de la règle) ; absent : texte générique. */
  why?: (actionId: string) => string | null | undefined;
  /** Libellé de la règle (« étape du projet ») pour une carte fermée d'elle-même. */
  ruleLabel?: (ruleKey: string | null) => string | null | undefined;
}

/**
 * Diff des actions touchées par un passage des règles. `before` : les lignes lues avant d'écrire (absentes :
 * nouvelles) ; `after` : les mêmes après écriture, plus les créées. Une ligne de `before` absente de `after`
 * est inchangée. Ordre : créées, rouvertes, réattribuées, puis fermées (chacune dans l'ordre de `after`).
 */
export function diffActions(
  before: readonly ActionState[],
  after: readonly ActionState[],
  reasons: DiffReasons = {},
): RulesChange[] {
  const previous = new Map(before.map((row) => [row.id, row]));
  const out: Record<ConsequenceKind, RulesChange[]> = {
    created: [],
    reopened: [],
    reassigned: [],
    closed: [],
  };
  const change = (kind: ConsequenceKind, row: ActionState, prev: ActionState | null, reason: string) =>
    out[kind].push({
      kind,
      actionId: row.id,
      title: row.title,
      assigneeId: row.assigneeId,
      previousAssigneeId: prev?.assigneeId ?? null,
      ruleKey: row.ruleKey,
      reason,
    });
  for (const row of after) {
    const prev = previous.get(row.id) ?? null;
    if (!prev) {
      if (isOpen(row.status))
        change('created', row, null, reasons.why?.(row.id) || 'Proposée par les règles');
      continue;
    }
    if (isOpen(prev.status) && !isOpen(row.status)) {
      const label = reasons.ruleLabel?.(row.ruleKey);
      change('closed', row, prev, label ? `Fermée d'elle-même : ${label}` : "Fermée d'elle-même");
    } else if (!isOpen(prev.status) && isOpen(row.status)) {
      change('reopened', row, prev, reasons.why?.(row.id) || 'Rouverte par les règles');
    } else if (isOpen(row.status) && prev.assigneeId !== row.assigneeId) {
      change('reassigned', row, prev, 'Réattribuée par les règles');
    }
  }
  return [...out.created, ...out.reopened, ...out.reassigned, ...out.closed];
}

/** Nouvelle pour la personne attribuée : créée, rouverte ou réattribuée. */
export const isNewForAssignee = (kind: ConsequenceKind) => kind !== 'closed';

/** Cartes d'une personne changées par un passage des règles. */
export interface PersonConsequences {
  userId: string;
  added: RulesChange[];
  closed: RulesChange[];
}

/**
 * Regroupe par personne attribuée ce qui la concerne, une entrée par personne (une notification par
 * personne et par passage). L'auteur du geste n'est jamais prévenu (il voit les conséquences à l'écran).
 * `user` : geste d'une personne (cartes ajoutées et fermées) ; `sync` : synchro (nouvelles cartes seulement,
 * les fermées n'apparaissent que dans « Fait cette semaine »).
 */
export function consequencesByPerson(
  changes: readonly Pick<RulesChange, 'kind' | 'assigneeId' | 'actionId' | 'title'>[],
  options: { mode: 'user' | 'sync'; actorId?: string | null },
): PersonConsequences[] {
  const byUser = new Map<string, PersonConsequences>();
  for (const c of changes) {
    if (!c.assigneeId || c.assigneeId === options.actorId) continue;
    const added = isNewForAssignee(c.kind);
    if (!added && options.mode === 'sync') continue;
    const entry = byUser.get(c.assigneeId) ?? { userId: c.assigneeId, added: [], closed: [] };
    (added ? entry.added : entry.closed).push(c as RulesChange);
    byUser.set(c.assigneeId, entry);
  }
  return [...byUser.values()];
}

/** Lien « À faire » centré sur ces cartes (`/a-faire?action=id1,id2`, 20 au plus). */
export function afaireFocusHref(actionIds: readonly string[]): string {
  return `/a-faire?action=${actionIds.slice(0, 20).join(',')}`;
}

/** Identifiants lus dans `?action=` (UUID seulement, 20 au plus). */
export function parseFocusIds(value: unknown): string[] {
  const raw = Array.isArray(value) ? value.join(',') : typeof value === 'string' ? value : '';
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(s))
    .slice(0, 20);
}

const LIST_MAX = 5;
function bullets(items: readonly { title: string }[]): string {
  const shown = items.slice(0, LIST_MAX).map((c) => `• ${c.title}`);
  if (items.length > LIST_MAX) shown.push(`• et ${items.length - LIST_MAX} autre(s)`);
  return shown.join('\n');
}

export interface ConsequenceNotification {
  kind: 'new_task' | 'task_closed';
  title: string;
  body: string;
  url: string;
}

/**
 * Notification groupée d'une personne. `cause` : « « Création du site » cochée par Maxime » (geste) ; null
 * pour une synchro. Cartes ajoutées (avec ou sans fermées) → `new_task` ; seulement fermées → `task_closed`.
 */
export function consequenceNotification(
  person: PersonConsequences,
  cause: string | null,
): ConsequenceNotification | null {
  const { added, closed } = person;
  if (added.length === 0 && closed.length === 0) return null;
  const why = cause
    ? `Suite à ${cause}.`
    : `Apparue${added.length > 1 ? 's' : ''} après la dernière synchro.`;
  if (added.length > 0) {
    const title =
      added.length === 1
        ? `Nouvelle tâche pour toi : ${added[0]!.title}`
        : `${added.length} nouvelles tâches pour toi`;
    const lines = [why];
    if (added.length > 1) lines.push(bullets(added));
    if (closed.length === 1) lines.push(`Carte fermée : ${closed[0]!.title}`);
    else if (closed.length > 1) lines.push(`${closed.length} cartes fermées :\n${bullets(closed)}`);
    return {
      kind: 'new_task',
      title,
      body: lines.join('\n'),
      url: afaireFocusHref(added.map((c) => c.actionId)),
    };
  }
  const title =
    closed.length === 1 ? `Tâche fermée : ${closed[0]!.title}` : `${closed.length} de tes tâches fermées`;
  return {
    kind: 'task_closed',
    title,
    body: closed.length > 1 ? `${why}\n${bullets(closed)}` : why,
    url: afaireFocusHref(closed.map((c) => c.actionId)),
  };
}

// ------------------------------------------------------------------ Textes de l'écran de validation

const forWhom = (c: Pick<ActionConsequence, 'assigneeId' | 'assigneeName'>, meId: string | null) =>
  c.assigneeId && c.assigneeId === meId ? 'toi' : c.assigneeName;

/** « Nouvelle carte pour Stéphane : Relire et tester le site · Plans & Cie », « Carte fermée : … ». */
export function consequenceLine(c: ActionConsequence, meId: string | null): string {
  const who = forWhom(c, meId);
  switch (c.kind) {
    case 'created':
      return who ? `Nouvelle carte pour ${who} : ${c.title}` : `Nouvelle carte : ${c.title}`;
    case 'reopened':
      return who ? `Carte rouverte pour ${who} : ${c.title}` : `Carte rouverte : ${c.title}`;
    case 'reassigned':
      return who ? `Carte réattribuée à ${who} : ${c.title}` : `Carte réattribuée : ${c.title}`;
    case 'closed':
      return `Carte fermée : ${c.title}`;
  }
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Résumé du toast : « 1 carte ajoutée pour Stéphane, 1 fermée » ; vide sans conséquence. */
export function consequencesSummary(consequences: readonly ActionConsequence[], meId: string | null): string {
  const groups = new Map<string, number>();
  for (const c of consequences) {
    if (!isNewForAssignee(c.kind)) continue;
    const key = forWhom(c, meId) ?? '';
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  const parts = [...groups.entries()].map(
    ([who, n]) => `${plural(n, 'carte ajoutée', 'cartes ajoutées')}${who ? ` pour ${who}` : ''}`,
  );
  const closed = consequences.filter((c) => c.kind === 'closed').length;
  if (closed > 0) {
    parts.push(
      parts.length > 0
        ? plural(closed, 'fermée', 'fermées')
        : plural(closed, 'carte fermée', 'cartes fermées'),
    );
  }
  return parts.join(', ');
}
